import React, { useState } from 'react';
import { List, ListItemText, Divider } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { errorMessages } from 'src/utils/properties.utils';
import {
  DropdownButton,
  DropdownIcon,
  SelectorContainer,
  PresetsContainer,
  CustomRangeContainer,
  PresetItem,
  ApplyButton,
  StyledPopover,
  PresetsTitle,
  CustomRangeTitle,
  DatesFlexContainer,
  ErrorMessage,
} from './DateSelector.styled';

export type DateRangePreset =
  | 'Today'
  | 'Yesterday'
  | 'Last 7 Days'
  | 'This Month'
  | 'Last Month'
  | 'Custom';

export interface DateRange {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  preset: DateRangePreset;
}

interface DateRangeSelectorProps {
  onRangeChange: (range: DateRange) => void;
  defaultPreset?: DateRangePreset;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  onRangeChange,
  defaultPreset = 'This Month',
  startDate = null,
  endDate = null,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedPreset, setSelectedPreset] =
    useState<DateRangePreset>(defaultPreset);
  const [customStart, setCustomStart] = useState<Dayjs | null>(
    defaultPreset === 'Custom' ? startDate : null
  );
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(
    defaultPreset === 'Custom' ? endDate : null
  );

  const isRangeTooLong = Boolean(
    customStart && customEnd && customEnd.diff(customStart, 'day') > 100
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  const handlePresetSelect = (preset: DateRangePreset) => {
    setSelectedPreset(preset);
    let start: Dayjs | null = null;
    let end: Dayjs | null = null;

    const today = dayjs();

    // Clear custom range if choosing a preset
    if (preset !== 'Custom') {
      setCustomStart(null);
      setCustomEnd(null);
    }

    switch (preset) {
      case 'Today':
        start = today.startOf('day');
        end = today.endOf('day');
        break;
      case 'Yesterday':
        start = today.subtract(1, 'day').startOf('day');
        end = today.subtract(1, 'day').endOf('day');
        break;
      case 'Last 7 Days':
        start = today.subtract(7, 'day').startOf('day');
        end = today.endOf('day');
        break;
      case 'This Month':
        start = today.startOf('month');
        end = today.endOf('day');
        break;
      case 'Last Month':
        start = today.subtract(1, 'month').startOf('month');
        end = today.subtract(1, 'month').endOf('month');
        break;
      case 'Custom':
        // Wait for user to select custom dates
        return;
    }

    onRangeChange({ startDate: start, endDate: end, preset });
    handleClose();
  };

  const handleCustomApply = () => {
    setSelectedPreset('Custom');
    onRangeChange({
      startDate: customStart,
      endDate: customEnd,
      preset: 'Custom',
    });
    handleClose();
  };

  return (
    <>
      <DropdownButton onClick={handleClick} variant="outlined">
        {selectedPreset}
        <DropdownIcon className="bx bx-caret-down" />
      </DropdownButton>

      <StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <SelectorContainer>
          <PresetsContainer>
            <PresetsTitle variant="subtitle2">Quick Presets</PresetsTitle>
            <List disablePadding>
              {[
                'Today',
                'Yesterday',
                'Last 7 Days',
                'This Month',
                'Last Month',
              ].map((preset) => (
                <PresetItem
                  key={preset}
                  selected={selectedPreset === preset}
                  onClick={() => handlePresetSelect(preset as DateRangePreset)}
                >
                  <ListItemText
                    primary={preset}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </PresetItem>
              ))}
            </List>
          </PresetsContainer>

          <Divider orientation="vertical" flexItem />

          <CustomRangeContainer>
            <CustomRangeTitle variant="subtitle2">
              Custom Range
            </CustomRangeTitle>
            <DatesFlexContainer>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={customStart}
                  onChange={(newValue) => setCustomStart(newValue)}
                  maxDate={customEnd || undefined}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="End Date"
                  value={customEnd}
                  onChange={(newValue) => setCustomEnd(newValue)}
                  minDate={customStart || undefined}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
              <ApplyButton
                variant="contained"
                fullWidth
                disabled={!customStart || !customEnd || isRangeTooLong}
                onClick={handleCustomApply}
              >
                Apply
              </ApplyButton>
              {isRangeTooLong && (
                <ErrorMessage>{errorMessages.DATE_RANGE_EXCEEDED}</ErrorMessage>
              )}
            </DatesFlexContainer>
          </CustomRangeContainer>
        </SelectorContainer>
      </StyledPopover>
    </>
  );
};

export default DateRangeSelector;
